import React from "react";
import { Form } from "../../";
import { useIntl } from "react-intl";
import createNumberMask from "text-mask-addons/dist/createNumberMask";

export interface CurrencyProps {
	label: string;
	hiddenLabel?: boolean;
	name: string;
	placeholder?: string;
	className?: string;
	id: string;
	currency: string;
	isFieldRequired?: boolean;
	onBlur?: (e: React.ChangeEvent) => void;
	onChange?: (e: React.ChangeEvent) => void;
	disabled?: boolean;
	defaultValue?: string;
	description?: React.ReactNode;
	hasError?: boolean;
	hideDecimals?: boolean;
	onInteract?: (data: { id: string; focused: boolean; hovered: boolean }) => void;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	fieldClassName?: string;
}

const CurrencyInput: React.FC<CurrencyProps> = ({
	label,
	hiddenLabel = false,
	name,
	placeholder,
	className,
	id,
	currency,
	isFieldRequired = false,
	onBlur,
	onChange,
	disabled,
	defaultValue,
	description,
	hasError,
	hideDecimals = false,
	onInteract,
	onKeyDown,
	fieldClassName,
}) => {
	const intl = useIntl();

	const v = intl.formatNumberToParts(1000.0, { style: "currency", currency: currency ?? "USD" });
	const symbol = v.find((x) => x.type === "currency")?.value || "$";
	const isCommaDecimal = v.find((x) => x.type === "decimal")?.value === ",";

	const mask = (rawValue: string) => {
		const defaultMaskOptions = {
			prefix: symbol,
			suffix: "",
			includeThousandsSeparator: true,
			thousandsSeparatorSymbol: v.find((x) => x.type === "group")?.value || ",",
			allowDecimal: true,
			decimalSymbol: v.find((x) => x.type === "decimal")?.value || ".",
			decimalLimit: 2, // how many digits allowed after the decimal
			integerLimit: 7, // limit length of integer numbers
			allowNegative: false,
			allowLeadingZeroes: parseInt(rawValue) === 0 ? true : false,
			requireDecimal: hideDecimals ? false : true,
		};
		const numberMask = createNumberMask(defaultMaskOptions);
		const mask = numberMask(rawValue);

		const decimalsRegex = isCommaDecimal ? /\,([0-9]{1,2})/ : /\.([0-9]{1,2})/;
		const result = decimalsRegex.exec(rawValue);
		// In case there is only one decimal
		if (!hideDecimals) {
			if (result && result[1].length < 2) {
				mask.push("0");
			} else if (!result) {
				mask.push("0");
				mask.push("0");
			}
		}

		return mask;
	};

	return (
		<Form.Field
			component={Form.Input.MaskedTextInput}
			onKeyDown={onKeyDown}
			mask={mask}
			className={className}
			label={label}
			hiddenLabel={hiddenLabel}
			name={name}
			id={id}
			placeholder={placeholder}
			isFieldRequired={isFieldRequired}
			onBlur={onBlur}
			onChange={onChange}
			disabled={disabled}
			defaultValue={defaultValue}
			description={description}
			hasError={hasError}
			onInteract={onInteract}
			fieldClassName={fieldClassName}
		/>
	);
};

export default CurrencyInput;
